// IE Hub - Enhanced Form Control Component
// Wrapper around MUI FormControl with standardized styling and validation

import React, { forwardRef } from 'react';
import {
  FormControl,
  FormHelperText,
  FormLabel,
  Typography
} from '@mui/material';

interface UIFormControlProps {
  label?: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  success?: boolean;
  children?: React.ReactNode;
  fullWidth?: boolean;
  margin?: 'none' | 'dense' | 'normal';
  size?: 'small' | 'medium';
  [key: string]: any;
}

const UIFormControl = forwardRef<HTMLDivElement, UIFormControlProps>((props, ref) => {
  const {
    label,
    required = false,
    error = false,
    helperText,
    success = false,
    children,
    fullWidth = true,
    margin = 'normal',
    size = 'medium',
    ...otherProps
  } = props;
  const getHelperTextColor = () => {
    if (error) return 'error.main';
    if (success) return 'success.main';
    return 'text.secondary';
  };
  
  const getHelperTextIcon = () => {
    if (error) return '⚠️';
    if (success) return '✅';
    return null;
  };
  
  return (
    <FormControl
      ref={ref}
      fullWidth={fullWidth}
      margin={margin}
      size={size}
      error={error}
      required={required}
      {...otherProps}
    >
      {label && (
        <FormLabel
          component="legend"
          sx={{
            fontSize: size === 'small' ? '0.875rem' : '1rem',
            fontWeight: 500,
            marginBottom: 1,
            color: error ? 'error.main' : 'text.primary',
            '&.Mui-focused': {
              color: error ? 'error.main' : 'primary.main'
            }
          }}
        >
          {label}
          {required && (
            <Typography
              component="span"
              sx={{
                color: 'error.main',
                marginLeft: 0.5,
                fontSize: '1rem'
              }}
            >
              *
            </Typography>
          )}
        </FormLabel>
      )}
      
      {children}
      
      {helperText && (
        <FormHelperText
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            color: getHelperTextColor(),
            fontSize: '0.75rem',
            marginTop: 0.5
          }}
        >
          {getHelperTextIcon() && (
            <span style={{ fontSize: '0.875rem' }}>
              {getHelperTextIcon()}
            </span>
          )}
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
});

UIFormControl.displayName = 'UIFormControl';

export default UIFormControl;
